import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, DatePicker, Space, message, Card, Table, Tag, Select } from "antd";
import jsPDF from 'jspdf';
import { Row, Form, Col } from "antd";
import { DownloadOutlined, FilterOutlined, ClearOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';

import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
// import FooterDashboard from "../common/FooterDashboard/FooterDashboard";
import dayjs from "dayjs";
import ReportsApi from "../common/Apis/ReportsApi";
import Pagination from "../common/Pagination/Pagination";
import "../common/Pagination/Pagination.css";

const CoolingProcessReport = () => {
    const { Content } = Layout;
    const [formAdd] = Form.useForm();
    const [coolingData, setCoolingData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Filter states - Selected values
    const [batchCodeFilter, setBatchCodeFilter] = useState(null);
    const [recordedByFilter, setRecordedByFilter] = useState(null);
    
    // Filter options
    const [batchOptions, setBatchOptions] = useState([]);
    const [recordedByOptions, setRecordedByOptions] = useState([]);
    
    // Pagination state using your custom pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Check if any filter is applied
    const hasActiveFilters = batchCodeFilter || recordedByFilter;

    // Display data - only show filteredData if filters are applied
    const displayData = hasActiveFilters ? filteredData : coolingData;

    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    }, []);

    // Extract unique filter options from data
    const extractFilterOptions = useCallback((data) => {
        const batches = new Set();
        const recordedBy = new Set();

        data.forEach((item) => {
            if (item?.productionDTO?.batchCode) {
                batches.add(item.productionDTO.batchCode);
            }
            if (item?.productionDTO?.createdBy) {
                recordedBy.add(item.productionDTO.createdBy);
            }
        });

        setBatchOptions(Array.from(batches).sort());
        setRecordedByOptions(Array.from(recordedBy).sort());
    }, []);

    // Apply filters
    const applyFilters = useCallback((data) => {
    if (!data || data.length === 0) {
        setFilteredData([]);
        setTotalItems(0);
        setTotalPages(0);
        return;
    }

    let filtered = [...data];

    if (batchCodeFilter) {
        filtered = filtered.filter(item =>
            item?.productionDTO?.batchCode &&
            item.productionDTO.batchCode.toLowerCase() === batchCodeFilter.toLowerCase()
        );
    }

    if (recordedByFilter) {
        filtered = filtered.filter(item =>
            item?.productionDTO?.createdBy &&
            item.productionDTO.createdBy.toLowerCase() === recordedByFilter.toLowerCase()
        );
    }

    setFilteredData(filtered);
    setTotalItems(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
    setCurrentPage(1);

}, [batchCodeFilter, recordedByFilter, pageSize]);

    useEffect(() => {
        applyFilters(coolingData);
    }, [batchCodeFilter, recordedByFilter, coolingData, applyFilters]);

    // Update everything when coolingData changes
    useEffect(() => {
        if (coolingData.length > 0) {
            extractFilterOptions(coolingData);
            applyFilters(coolingData);
        } else {
            setFilteredData([]);
            setTotalItems(0);
            setTotalPages(0);
            setBatchOptions([]);
            setRecordedByOptions([]);
        }
    }, [coolingData, extractFilterOptions, applyFilters]);

    // Reset filters
    const resetFilters = () => {
        setBatchCodeFilter(null);
        setRecordedByFilter(null);
        setFilteredData([]);
        setTotalItems(coolingData.length);
        setTotalPages(Math.ceil(coolingData.length / pageSize));
        setCurrentPage(1);
        message.info("All filters have been reset");
    };

    // Function to handle export to PDF
    const handleExportToPdf = () => {
        const dataToExport = displayData;
        
        if (!dataToExport || dataToExport.length === 0) {
            message.warning("No data available to export");
            return;
        }

        setExportLoading(true);

        try {
            const pdfData = [];

            dataToExport.forEach((item) => {
                let correctiveAction = item?.correctiveAction || '-';
                
                if (correctiveAction !== '-') {
                    if (correctiveAction.toLowerCase().includes('rework')) {
                        correctiveAction = 'Rework';
                    } else if (correctiveAction.toLowerCase().includes('reject')) {
                        correctiveAction = 'Rejected';
                    } else if (correctiveAction.toLowerCase().includes('approved')) {
                        correctiveAction = 'Approved';
                    }
                }

                pdfData.push([
                    formatDate(item.createdDate),
                    formatDateTime(item.temperatureTakenTime1),
                    formatDateTime(item.temperatureTakenTime2),
                    item?.productionDTO?.batchCode || '-',
                    item?.productionName || '-',
                    item?.temperatureInCelsius1 || '-',
                    item?.temperatureInCelsius2 || '-',
                    formatDate(item.ubd),
                    correctiveAction,
                    item?.productionDTO?.createdBy || '-'
                ]);
            });

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            doc.setFontSize(16);
            doc.text('Cooling Process Report', 14, 15);

            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');

            let filterInfo = '';
            if (startDate && endDate) {
                filterInfo += `Date Range: ${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`;
            }
            if (batchCodeFilter) {
                filterInfo += ` | Batch: ${batchCodeFilter}`;
            }
            if (recordedByFilter) {
                filterInfo += ` | Recorded By: ${recordedByFilter}`;
            }
            filterInfo += ` | Total Records: ${dataToExport.length}`;

            doc.setFontSize(10);
            doc.text(filterInfo, 14, 22);

            autoTable(doc, {
                startY: 28,
                head: [[
                    'Date',
                    'Temp 1 Time',
                    'Temp 2 Time',
                    'Batch Code',
                    'Product Description',
                    'Temp 1(°C)',
                    'Temp 2(°C)',
                    'UBD',
                    'Corrective Action',
                    'Recorded By'
                ]],
                body: pdfData,
                styles: {
                    fontSize: 7,
                    cellPadding: 2
                },
                headStyles: {
                    fillColor: [79, 129, 189],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold'
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                margin: {
                    left: 5,
                    right: 5
                }
            });

            let fileName = `Cooling_Process_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;

            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                const batchStr = batchCodeFilter ? `_${batchCodeFilter}` : '';
                const recordedStr = recordedByFilter ? `_${recordedByFilter}` : '';
                fileName = `Cooling_Process_Report_${startStr}_to_${endStr}${batchStr}${recordedStr}.pdf`;
            }

            doc.save(fileName);

            message.success("Cooling process report exported successfully!");

        } catch (error) {
            console.error("Error exporting cooling process report:", error);
            message.error("Failed to export cooling process report");
        } finally {
            setExportLoading(false);
        }
    };

    // Function to handle export to Excel
    const handleExportToExcel = () => {
        const dataToExport = displayData;
        
        if (!dataToExport || dataToExport.length === 0) {
            message.warning("No data available to export");
            return;
        }

        setExportLoading(true);
        
        try {
            const excelData = [];

            // Process data for Excel export
            dataToExport.forEach((item, index) => {
                let correctiveAction = item?.correctiveAction || '-';
                
                if (correctiveAction !== '-') {
                    if (correctiveAction.toLowerCase().includes('rework')) {
                        correctiveAction = 'Rework';
                    } else if (correctiveAction.toLowerCase().includes('reject')) {
                        correctiveAction = 'Rejected';
                    } else if (correctiveAction.toLowerCase().includes('approved')) {
                        correctiveAction = 'Approved';
                    }
                }

                excelData.push({
                    'Date': formatDate(item.createdDate),
                    'Temp 1 Time': formatDateTime(item.temperatureTakenTime1),
                    'Temp 2 Time': formatDateTime(item.temperatureTakenTime2),
                    'Batch Code': item?.productionDTO?.batchCode || '-',
                    'Product Description': item?.productionName || '-',
                    'Temp 1(°C)': item?.temperatureInCelsius1 || '-',
                    'Temp 2(°C)': item?.temperatureInCelsius2 || '-',
                    'UBD': formatDate(item.ubd),
                    'Corrective Action': correctiveAction,
                    'Recorded By': item?.productionDTO?.createdBy || '-'
                });
            });

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 12 },  // Date
                { wch: 15 },  // Temp 1 Time
                { wch: 15 },  // Temp 2 Time
                { wch: 15 },  // Batch Code
                { wch: 25 },  // Product Description
                { wch: 15 },  // Temp 1(°C)
                { wch: 15 },  // Temp 2(°C)
                { wch: 12 },  // UBD
                { wch: 20 },  // Corrective Action
                { wch: 15 }   // Recorded By
            ];
            ws['!cols'] = colWidths;

            // Style the header row
            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const address = XLSX.utils.encode_col(C) + '1';
                if (!ws[address]) continue;
                ws[address].s = {
                    font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
                    fill: { fgColor: { rgb: "4F81BD" }, patternType: "solid" },
                    alignment: { horizontal: "center", vertical: "center" }
                };
            }

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Cooling Process Report");

            // Get date range for filename
            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');
            let fileName = `Cooling_Process_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
            
            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                const batchStr = batchCodeFilter ? `_${batchCodeFilter}` : '';
                const recordedStr = recordedByFilter ? `_${recordedByFilter}` : '';
                fileName = `Cooling_Process_Report_${startStr}_to_${endStr}${batchStr}${recordedStr}.xlsx`;
            }

            // Save file
            XLSX.writeFile(wb, fileName);
            
            message.success("Cooling process report exported successfully as Excel!");
        } catch (error) {
            console.error("Error exporting cooling process report:", error);
            message.error("Failed to export cooling process report");
        } finally {
            setExportLoading(false);
        }
    };

    const handlePrintButtonClick2 = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const fontSize = 12;
        const margin = 5;
        const cellWidthFactor = 0.295;
        const cellHeightFactor = 0.2;

        doc.setFontSize(fontSize);

        const table = document.querySelector('.table');
        const rows = table.querySelectorAll('tr');

        const pageTitle = 'Cooling Process Report';
        const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
        const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
        doc.text(pageTitleXPos, margin, pageTitle);

        doc.setFontSize(fontSize);
        const currentDate = new Date().toLocaleString();
        const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
        const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
        doc.text(dateTimeXPos, margin, currentDate);

        let yPos = margin + fontSize;

        rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            let xPos = margin;

            cells.forEach((cell, cellIndex) => {
                const cellText = cell.textContent.trim();
                const cellWidth = cell.offsetWidth * cellWidthFactor;
                const cellHeight = row.offsetHeight * cellHeightFactor;

                if (yPos + cellHeight > doc.internal.pageSize.height - margin) {
                    doc.addPage();
                    yPos = margin + fontSize;
                    const pageTitle = 'Cooling Process Report';
                    const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
                    const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
                    doc.text(pageTitleXPos, margin, pageTitle);

                    doc.setFontSize(fontSize);
                    const currentDate = new Date().toLocaleString();
                    const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
                    const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
                    doc.text(dateTimeXPos, margin, currentDate);
                }

                const isHeader = cell.tagName.toLowerCase() === 'th';
                const fontWeight = isHeader ? 'bold' : 'normal';

                doc.setFont(undefined, fontWeight);

                const textWidth = doc.getStringUnitWidth(cellText) * fontSize / doc.internal.scaleFactor;
                const cellPadding = 2;
                let xPosAlign = xPos + (cellWidth - textWidth - cellPadding) / 2;

                if (!isHeader) {
                    xPosAlign = xPos;
                }
                doc.rect(xPos, yPos, cellWidth, cellHeight);
                doc.text(xPosAlign + 1, yPos + 5, cellText);

                xPos += cellWidth;
            });

            yPos += row.offsetHeight * cellHeightFactor;
        });

        window.open(doc.output('bloburl'), '_blank');
    };

    const fetchCoolingData = useCallback(async (page = 1, pageSize = 10) => {
        const startDate = formAdd.getFieldValue('startDate');
        const endDate = formAdd.getFieldValue('endDate');
        
        if (!startDate || !endDate) {
            return;
        }
        
        try {
            PageLoading();
            const startDateStr = dayjs(startDate).format("DD/MM/YYYY");
            const endDateStr = dayjs(endDate).format("DD/MM/YYYY");
            const startTimestamp = convertToUnixTimestamp(startDateStr);
            const endTimestamp = convertToUnixTimestamp(endDateStr) + (24 * 60 * 60 * 1000); 
            
            const response = await ReportsApi.CoolingProcessReport(startTimestamp, endTimestamp);
            
            let dataArray = [];
            let totalElements = 0;
            
            if (response?.payload && Array.isArray(response.payload)) {
                dataArray = response.payload;
                totalElements = response.payload.length;
            } else if (response?.payloadDto?.content) {
                dataArray = response.payloadDto.content;
                totalElements = response.payloadDto.totalElements;
            } else if (Array.isArray(response)) {
                dataArray = response;
                totalElements = response.length;
            } else if (response?.content) {
                dataArray = response.content;
                totalElements = response.totalElements;
            }
            
            setCoolingData(dataArray);
            // Reset filters when new data is loaded
            setBatchCodeFilter(null);
            setRecordedByFilter(null);
            setFilteredData([]);
            setTotalItems(totalElements);
            setTotalPages(Math.ceil(totalElements / pageSize));
            setCurrentPage(page);
            setPageSize(pageSize);
        } catch (err) {
            console.log(err);
            if (err.apiError?.status) {
                notification.open({
                    message: (
                        <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                    ),
                    description: err.apiError.message,
                    closeIcon: false,
                    duration: 3,
                    style: {
                        backgroundColor: "#fff2f0",
                        color: "red",
                    },
                });
            } else {
                notification.error({
                    message: "Error",
                    description: err?.message || "Failed to fetch cooling process report",
                });
            }
        }
    }, [PageLoading]);

    const handleFilter = (data) => {
        setFilterLoading(true);
        fetchCoolingData(1, pageSize);
        setTimeout(() => {
            setFilterLoading(false);
        }, 800);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    function formatDate(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        const options = { day: "2-digit", month: "short", year: "numeric" };
        return date.toLocaleDateString("en-US", options);
    }

    function formatDateTime(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp * 1000);
        if (isNaN(date.getTime())) return '-';
        const options = { hour: '2-digit', minute: '2-digit' };
        return date.toLocaleTimeString("en-US", options);
    }

    function convertToUnixTimestamp(dateString) {
        const parts = dateString.split("/");
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const unixTimestamp = Date.UTC(year, month, day);
        return unixTimestamp;
    }

    const handleClear = () => {
        setIsLoading(true);
        setCoolingData([]);
        setFilteredData([]);
        // Reset filters
        setBatchCodeFilter(null);
        setRecordedByFilter(null);
        getCurrent();
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    };

    const getCurrent = useCallback(() => {
        const todayDate = dayjs().format('DD/MM/YYYY');
        const tomorrowDate = dayjs().add(1, 'day').format('DD/MM/YYYY');

        formAdd.setFieldsValue({
            startDate: dayjs(todayDate, 'DD/MM/YYYY'),
            endDate: dayjs(tomorrowDate, 'DD/MM/YYYY'),
        });
        
        fetchCoolingData(1, 10);
    }, [fetchCoolingData]);

    useEffect(() => {
        getCurrent();
    }, [getCurrent]);

    const getCorrectiveActionTag = (correctiveAction) => {
        if (!correctiveAction || correctiveAction === 'N/A') return '-';
        if (correctiveAction.toLowerCase().includes('rework')) {
            return <Tag color="orange">Rework</Tag>;
        } else if (correctiveAction.toLowerCase().includes('reject')) {
            return <Tag color="red">Rejected</Tag>;
        } else if (correctiveAction.toLowerCase().includes('approved')) {
            return <Tag color="green">Approved</Tag>;
        }
        return <Tag color="blue">{correctiveAction}</Tag>;
    };

    // Get current page data for client-side pagination
    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return displayData.slice(startIndex, endIndex);
    };

    const currentPageData = getCurrentPageData();

    // Table columns configuration
    const columns = [
        {
            title: 'Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: '10%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Temp 1 Time',
            dataIndex: 'temperatureTakenTime1',
            key: 'temperatureTakenTime1',
            width: '10%',
            render: (time) => formatDateTime(time),
        },
        {
            title: 'Temp 2 Time',
            dataIndex: 'temperatureTakenTime2',
            key: 'temperatureTakenTime2',
            width: '10%',
            render: (time) => formatDateTime(time),
        },
        {
            title: 'Batch Code',
            dataIndex: ['productionDTO', 'batchCode'],
            key: 'batchCode',
            width: '12%',
            render: (code) => {
                if (!code) return '-';
                return (
                    <div style={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '200px',
                        padding: '0px 0px 20px',
                        display: 'block',
                    }}>
                        {code}
                    </div>
                );
            },
        },
        {
            title: 'Product Description',
            dataIndex: 'productionName',
            key: 'productionName',
            width: '12%',
            render: (text) => text || '-',
        },
        {
            title: 'Temp 1(°C)',
            dataIndex: 'temperatureInCelsius1',
            key: 'temperatureInCelsius1',
            width: '8%',
            render: (temp) => temp || '-',
        },
        {
            title: 'Temp 2(°C)',
            dataIndex: 'temperatureInCelsius2',
            key: 'temperatureInCelsius2',
            width: '8%',
            render: (temp) => temp || '-',
        },
        {
            title: 'UBD',
            dataIndex: 'ubd',
            key: 'ubd',
            width: '10%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Corrective Action',
            dataIndex: 'correctiveAction',
            key: 'correctiveAction',
            width: '10%',
            render: (action) => getCorrectiveActionTag(action),
        },
        {
            title: 'Recorded By',
            dataIndex: ['productionDTO', 'createdBy'],
            key: 'createdBy',
            width: '10%',
            render: (name) => name || '-',
        },
    ];

    const centeredColumns = columns.map((col) => ({
        ...col,
        align: 'center',
    }));

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader onPrintResponse2={handlePrintButtonClick2} />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <Content
                    className="form-content nav-content"
                    style={{
                        margin: "14px 16px 0",
                        padding: "30px",
                        background: "white",
                    }}
                >
                    <div>
                        {/* Filter Section - Separated Card */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Date Range Filter</span>
                                </Space>
                            </div>
                            
                            <Form
                                form={formAdd}
                                name="cooling_process_filter_form"
                                layout="vertical"
                                onFinish={handleFilter}
                            >
                                <Row gutter={[16, 0]}>
                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="startDate"
                                            label="Start Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Start Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select start date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="endDate"
                                            label="End Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select End Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select end date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={24} sm={24} xs={24}>
                                        <Form.Item label="Actions">
                                            <Space size="middle" wrap>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={filterLoading}
                                                    icon={<FilterOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Apply Date Filter
                                                </Button>
                                                <Button
                                                    onClick={() => handleClear()}
                                                    icon={<ClearOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Clear All
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>

                        {/* Advanced Filters Section */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                background: '#fafafa'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <SearchOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Advanced Filters</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        (Filter by Batch Code and Recorded By)
                                    </span>
                                </Space>
                            </div>
                            
                            <Row gutter={[16, 16]}>
                                <Col lg={12} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Batch Code
                                    </div>
                                    <Select
                                        placeholder="All Batches"
                                        style={{ width: '100%' }}
                                        value={batchCodeFilter}
                                        onChange={setBatchCodeFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {batchOptions.map(batch => (
                                            <Select.Option key={batch} value={batch}>{batch}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={12} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Recorded By
                                    </div>
                                    <Select
                                        placeholder="All Recorded By"
                                        style={{ width: '100%' }}
                                        value={recordedByFilter}
                                        onChange={setRecordedByFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {recordedByOptions.map(name => (
                                            <Select.Option key={name} value={name}>{name}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                            
                            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                <Space>
                                
                                    <Button
                                        onClick={resetFilters}
                                        icon={<ClearOutlined />}
                                        style={{ height: '36px' }}
                                        disabled={!hasActiveFilters}
                                    >
                                        Reset Filters
                                    </Button>
                                </Space>
                                <div style={{ padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '12px', color: '#1890ff' }}>
                                    <strong>Total Records:</strong> {coolingData.length} | 
                                    <strong> Displaying:</strong> {displayData.length} records
                                    {hasActiveFilters && (
                                        <span style={{ marginLeft: '8px' }}>
                                            (Filtered by: 
                                            {batchCodeFilter && ` Batch: ${batchCodeFilter}`}
                                            {recordedByFilter && `, Recorded By: ${recordedByFilter}`}
                                            )
                                        </span>
                                    )}
                                    {hasActiveFilters && displayData.length === 0 && (
                                        <span style={{ color: '#ff4d4f', marginLeft: '8px' }}>
                                            ⚠️ No records match the selected filters
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* Report Results Section */}
                        <Card 
                            style={{ 
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ 
                                marginBottom: '16px', 
                                borderBottom: '1px solid #f0f0f0', 
                                paddingBottom: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Space>
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Cooling Process Report Results</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {displayData.length > 0 && `(${displayData.length} records found)`}
                                        {hasActiveFilters && displayData.length === 0 && 
                                            ` (No matching records)`
                                        }
                                    </span>
                                </Space>
                                <Space>
                                    <Button
                                        type="primary"
                                        onClick={handleExportToPdf}
                                        loading={exportLoading}
                                        icon={<DownloadOutlined />}
                                        style={{ 
                                            height: '36px',
                                            backgroundColor: '#52c41a',
                                            borderColor: '#52c41a'
                                        }}
                                        disabled={displayData.length === 0}
                                    >
                                        Export Cooling Report (PDF)
                                    </Button>
                                </Space>
                            </div>
                            
                            <div className="dateFilterTable" style={{ overflowX: 'auto' }}>
                                <Table
                                    columns={centeredColumns}
                                    dataSource={currentPageData}
                                    rowKey="id"
                                    pagination={false}
                                    bordered
                                    className="users-table Reportable"
                                    scroll={{ x: 'max-content' }}
                                    locale={{
                                        emptyText: hasActiveFilters ? 'No records match the selected filters' : 'No data available'
                                    }}
                                />
                            </div>
                            
                            {/* Custom Pagination */}
                            {displayData.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        maxVisiblePages={5}
                                        showPageInfo={true}
                                        totalItems={totalItems}
                                        pageSize={pageSize}
                                    />
                                </div>
                            )}
                        </Card>
                    </div>
                </Content>

                {/* <FooterDashboard /> */}
            </Layout>
        </Layout>
    );
};

export default CoolingProcessReport;